"use client"

import { useState, useEffect, useRef, Suspense, useCallback } from "react"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import { SearchParamsHydrationGate } from "@/components/ssr/search-params-hydration-gate"
import { ArrowRight, CalendarCheck2, Droplet, MapPin, Ruler, Settings2, ShieldAlert, Sprout } from "lucide-react"
import {
  WeedAndFeedForm,
  type WeedAndFeedCurrentValues,
  type WeedAndFeedInitialValues,
} from "@/components/tools/weed-and-feed-form"
import {
  HeadlineHero,
  RelatedTools,
  ResultEmailCaptureCard,
  ResultSuccessCard,
  ToolPageLoadingFallback,
  ToolPageShell,
  ToolWideRegion,
} from "@/components/tools/shared"
import type { ResultSuccessCardStat } from "@/components/tools/shared"
import { GRASS_DISPLAY, getWeedAndFeedProduct, type GrassId } from "@/data/weed-and-feed-products"
import type { WeedAndFeedResult } from "@/lib/weed-and-feed-calculator"
import { analytics, stashEmailCaptureAttribution, toolNavigationAnalytics, trackEvent } from "@/lib/analytics"
import { useImpressionEvent } from "@/hooks/useImpressionEvent"
import { buildRelatedToolUrl, buildToolUrl } from "@/lib/result-tool-urls"
import { useCalculatorStarted } from "@/hooks/useCalculatorStarted"

const GRASS_PARAM: Record<string, GrassId> = {
  bermuda: "bermuda",
  bermudagrass: "bermuda",
  zoysia: "zoysia",
  zoysiagrass: "zoysia",
  staug: "staug",
  staugustine: "staug",
  staugustinegrass: "staug",
  centipede: "centipede",
  centipedegrass: "centipede",
  fescue: "fescue",
  tallfescue: "fescue",
  kbg: "kbg",
  kentuckybluegrass: "kbg",
  bluegrass: "kbg",
}

const LAWN_SIZE_MIN = 100
const LAWN_SIZE_MAX = 217800

function normalizeGrass(raw: string): GrassId | undefined {
  const key = raw.toLowerCase().replace(/[-_.\s]+/g, "")
  return GRASS_PARAM[key]
}

function parseLawnSizeParam(raw: string | null): number | undefined {
  if (!raw || !/^\d+$/.test(raw)) return undefined
  const size = Number(raw)
  if (!Number.isSafeInteger(size) || size < LAWN_SIZE_MIN || size > LAWN_SIZE_MAX) {
    return undefined
  }
  return size
}

const GRASS_ID_CTA_LOCATION = "weed_and_feed_grass_id_cross_sell"

function WeedAndFeedContent() {
  const searchParams = usePrerenderSafeSearchParams()
  const [initialValues, setInitialValues] = useState<WeedAndFeedInitialValues | null>(null)
  const [currentValues, setCurrentValues] = useState<WeedAndFeedCurrentValues | null>(null)
  const [liveResult, setLiveResult] = useState<WeedAndFeedResult | null>(null)
  const trackedRef = useRef(false)
  // calculator_started on first input — result is gated on a known region (ZIP).
  const startedRef = useCalculatorStarted('weed-and-feed')

  // URL prefill: ?zip & size & grass & product
  useEffect(() => {
    const zipParam = searchParams.get("zip")
    const sizeParam = searchParams.get("size")
    const grassParam = searchParams.get("grass")
    const productParam = searchParams.get("product")

    if (!zipParam && !sizeParam && !grassParam && !productParam) return

    const size = parseLawnSizeParam(sizeParam)
    const next: WeedAndFeedInitialValues = {
      zipCode: zipParam && /^\d{5}$/.test(zipParam) ? zipParam : undefined,
      lawnSize: size,
      grassType: grassParam ? normalizeGrass(grassParam) : undefined,
      productId: productParam && getWeedAndFeedProduct(productParam) ? productParam : undefined,
    }
    setInitialValues(next)
  }, [searchParams])

  const handleResultChange = useCallback((result: WeedAndFeedResult, values: WeedAndFeedCurrentValues) => {
    setLiveResult(result)
    setCurrentValues(values)
  }, [])

  // Fire calculator_used once, when a usable result (known region) first appears.
  useEffect(() => {
    if (!liveResult || trackedRef.current || !liveResult.regionKnown) return
    trackedRef.current = true
    analytics.calculatorUsed("weed-and-feed", {
      inputs: {
        product_id: liveResult.product.id,
        region: liveResult.region,
      },
      result: {
        safe: liveResult.safety.safe,
        amount_kind: liveResult.amount?.kind ?? undefined,
      },
      source: "live_update",
    })
  }, [liveResult])

  // The grass-ID flow lives on the homepage funnel.
  const grassIdHref = "/"

  // Impression denominator for the cross-sell's cta_clicked. Same cta_location
  // string on both events so PostHog can compute the rate.
  const grassIdCtaRef = useImpressionEvent<HTMLDivElement>(
    GRASS_ID_CTA_LOCATION,
    { cta_text: "Identify my grass" },
    { emit: analytics.ctaSurfaceShown },
  )

  return (
    <ToolPageShell breadcrumbLabel="Weed & Feed Calculator" rootRef={startedRef}>
      <HeadlineHero
        eyebrow="Timing by ZIP code"
        eyebrowIcon={<MapPin className="h-3.5 w-3.5" aria-hidden="true" />}
        title="When to apply weed & feed, and"
        accent="how much to buy"
        subtitle="Enter your ZIP, pick your product, and add your lawn size. You'll get your regional timing windows, bags or quarts needed, spreader settings, and grass-safety checks."
      />

      {/* The form's plan rail sits beside the inputs from 720px, so it needs
          the wide region. */}
      <ToolWideRegion>
        <WeedAndFeedForm initialValues={initialValues} onResultChange={handleResultChange} resultTargetId="weed-and-feed-result" />
      </ToolWideRegion>

      {liveResult && liveResult.regionKnown && (
        <div id="weed-and-feed-result" className="mt-8 scroll-mt-20 space-y-6">
          <WeedAndFeedSuccessCard result={liveResult} values={currentValues} />
          <ResultEmailCaptureCard
            source="tool_result_weed_and_feed"
            toolDisplayName="weed & feed schedule"
            subcopy="Spring and fall application reminders for your region, sent to your inbox."
            zipCode={currentValues?.zipCode ?? initialValues?.zipCode}
            accountLead={{
              redirectTo: buildToolUrl("weed-and-feed-calculator", {
                zip: currentValues?.zipCode ?? initialValues?.zipCode,
                lawnSize: currentValues?.lawnSize ?? initialValues?.lawnSize,
              }),
              headline: "Save my weed & feed schedule",
              subcopy: "Keep this weed & feed schedule in a free account and track your lawn over time.",
              onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_weed_and_feed"),
            }}
            successSubcopy="Your lawn tips are on the way. Your weed & feed schedule stays right here to revisit anytime."
            onCaptured={() => {}}
          />
        </div>
      )}

      {/* Grass-ID cross-sell (design spec): weed & feed safety hinges on
          knowing your grass, so route unsure users to the ID flow. */}
      <div
        ref={grassIdCtaRef}
        className="mt-8 flex flex-wrap items-center gap-4 rounded-2xl border border-gray-200 bg-white px-6 py-4.5 shadow-elevation-1"
      >
        <Sprout className="h-6 w-6 shrink-0 text-green-600" aria-hidden="true" />
        <p className="m-0 min-w-60 flex-1 text-sm text-gray-700">
          <strong className="text-gray-900">Not sure what grass you have?</strong> Snap a photo and we&apos;ll
          identify it, then check it against your weed &amp; feed automatically.
        </p>
        <a
          href={grassIdHref}
          onClick={() =>
            trackEvent("cta_clicked", {
              cta_location: GRASS_ID_CTA_LOCATION,
              cta_text: "Identify my grass",
            })
          }
          className="press inline-flex min-h-11 items-center gap-2 whitespace-nowrap rounded-full border border-green-200 bg-green-50 px-5 text-sm font-bold text-green-800 no-underline hover:border-green-300 hover:bg-green-100"
        >
          Identify my grass
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </a>
      </div>

      <RelatedTools
        className="mt-10"
        currentToolId="weed-and-feed-calculator"
        prefillValues={{
          zip: currentValues?.zipCode ?? initialValues?.zipCode,
          lawnSize: currentValues?.lawnSize ?? initialValues?.lawnSize,
        }}
      />
    </ToolPageShell>
  )
}

/**
 * Peak frame for the weed & feed plan: how much to buy (hero), a sentence
 * naming the next window, lawn size, grass and region, and one next step. An
 * unsafe product/grass pair gets no hero or CTA: the plan rail above already
 * lists the safe swaps.
 */
function WeedAndFeedSuccessCard({
  result,
  values,
}: {
  result: WeedAndFeedResult
  values: WeedAndFeedCurrentValues | null
}) {
  const grassName = values?.grassType ? GRASS_DISPLAY[values.grassType] : undefined
  const lawnSize = values?.lawnSize ?? 0
  const sizeLabel = lawnSize.toLocaleString()

  if (!result.safety.safe) {
    return (
      <ResultSuccessCard
        eyebrow="Not safe for your grass"
        eyebrowIcon={ShieldAlert}
        interpretation={
          <>
            {result.product.name} is not labeled for {grassName ?? "your grass"}. Switch to one of the safe
            products in your plan above before you buy.
          </>
        }
      />
    )
  }

  const next =
    result.windows.find((w) => w.urgency === "apply-now") ??
    result.windows.find((w) => w.urgency === "upcoming" || w.urgency === "running-late") ??
    result.windows[0]

  const amount = result.amount
  const hero =
    amount?.kind === "granular"
      ? { value: String(amount.bagsNeeded), unit: amount.bagsNeeded === 1 ? "bag" : "bags" }
      : amount?.kind === "liquid"
        ? {
            value: String(amount.containersNeeded),
            unit: `${amount.containerLabel}${amount.containersNeeded === 1 ? "" : "s"}`,
          }
        : null
  const buyPhrase =
    amount?.kind === "granular"
      ? `${amount.bagsNeeded} ${amount.bagsNeeded === 1 ? "bag" : "bags"} of the ${amount.bagCoverageSqFt.toLocaleString()} sq ft size`
      : amount?.kind === "liquid"
        ? `${amount.containersNeeded} ${amount.containerLabel}${amount.containersNeeded === 1 ? "" : "s"}`
        : null

  const timingSentence = !next
    ? null
    : next.urgency === "apply-now"
      ? `Apply now: your ${next.displayLabel.toLowerCase()} window runs ${next.window}.`
      : next.urgency === "too-late"
        ? `Your ${next.displayLabel.toLowerCase()} window (${next.window}) has passed for this year.`
        : `Your next window is ${next.displayLabel.toLowerCase()}, ${next.window}.`

  const stats: ResultSuccessCardStat[] = []
  if (next) stats.push({ icon: CalendarCheck2, label: `${next.displayLabel} window`, value: next.window })
  const setting = result.spreaderSettings?.[0]
  if (setting) stats.push({ icon: Settings2, label: "Spreader setting", value: setting.setting })
  if (amount?.kind === "granular") {
    stats.push({ icon: Ruler, label: "Bags cover", value: `${amount.totalCoverageSqFt.toLocaleString()} sq ft` })
  } else if (amount?.kind === "liquid") {
    stats.push({ icon: Droplet, label: "Spray mix", value: `${amount.gallonsOfMix} gal` })
  }

  const zip = values?.zipCode
  const herbicideHref = buildRelatedToolUrl("herbicide-timing", "weed-and-feed-calculator", {
    zip,
    grassType: grassName,
    lawnSize: lawnSize || undefined,
  })

  const shareBase = buildToolUrl("weed-and-feed-calculator", { zip, lawnSize: lawnSize || undefined })
  const shareParams = new URLSearchParams({ product: result.product.id })
  if (values?.grassType) shareParams.set("grass", values.grassType)
  const shareUrl = `${shareBase}${shareBase.includes("?") ? "&" : "?"}${shareParams.toString()}`

  return (
    <ResultSuccessCard
      heroValue={hero?.value}
      heroUnit={hero?.unit}
      heroLabel={hero ? `of ${result.product.name}` : undefined}
      interpretation={
        <>
          {timingSentence}
          {buyPhrase && (
            <>
              {" "}
              Buy {buyPhrase} for your {sizeLabel} sq ft {grassName ? `${grassName} ` : ""}lawn in the {result.region}.
            </>
          )}
        </>
      }
      stats={stats}
      cta={{
        href: herbicideHref,
        label: "Time crabgrass pre-emergent for my ZIP",
        onClick: () =>
          toolNavigationAnalytics.relatedToolClicked({
            sourceTool: "weed-and-feed-calculator",
            targetTool: "herbicide-timing",
            rank: 1,
            placement: "success_card",
            recommendationReason: "Pre-emergent covers the grassy weeds weed & feed misses",
            prefillFields: [zip && "zip", grassName && "grass", lawnSize && "size"].filter(Boolean).join(","),
          }),
      }}
      share={{
        url: shareUrl,
        title: "My weed & feed plan",
        text: `${result.product.name} plan for my ${sizeLabel} sq ft lawn`,
        trackingId: "weed-and-feed-calculator",
      }}
    />
  )
}

export function WeedAndFeedCalculatorPageContent() {
  return (
    <Suspense fallback={<ToolPageLoadingFallback toolId="weed-and-feed-calculator" />}>
      <SearchParamsHydrationGate dateSensitive>
        <WeedAndFeedContent />
      </SearchParamsHydrationGate>
    </Suspense>
  )
}
